import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Quote, Send, BadgeCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    relation: "",
    content: "",
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, formData);
      toast.success("Thank you for your review! It will appear shortly.");
      setFormData({ name: "", relation: "", content: "", rating: 5 });
      setShowForm(false);
      fetchTestimonials();
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="testimonials-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 relative">
        <div className="absolute top-0 left-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl -z-10" />
        
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
              Testimonials
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-primary leading-tight mb-6">
              What Our Clients Say
            </h1>
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-7 h-7 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-muted-foreground text-lg mb-6">
              {testimonials.length} verified review{testimonials.length === 1 ? "" : "s"} from our valued clients
            </p>
            <Button
              onClick={() => setShowForm(!showForm)}
              data-testid="write-review-btn"
              className="bg-accent hover:bg-accent/90 text-white rounded-full px-6 py-3"
            >
              Write a Review
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Review Form */}
      {showForm && (
        <section className="py-8">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto bg-white border border-primary/10 rounded-2xl p-8"
            >
              <h2 className="font-serif text-2xl font-semibold text-primary mb-6">
                Share Your Experience
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Your name"
                      className="h-12 rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="relation">Your Relationship *</Label>
                    <Select
                      value={formData.relation}
                      onValueChange={(value) => setFormData({ ...formData, relation: value })}
                    >
                      <SelectTrigger className="h-12 rounded-lg">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Client">Client / Service User</SelectItem>
                        <SelectItem value="Son of Client">Son of Client</SelectItem>
                        <SelectItem value="Daughter of Client">Daughter of Client</SelectItem>
                        <SelectItem value="Spouse of Client">Spouse of Client</SelectItem>
                        <SelectItem value="Family Member">Family Member</SelectItem>
                        <SelectItem value="Next of Kin">Next of Kin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Rating *</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="p-1"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= formData.rating
                              ? "text-yellow-400 fill-current"
                              : "text-gray-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Your Review *</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    placeholder="Tell us about your experience with Konastone Healthcare..."
                    rows={4}
                    className="rounded-lg"
                  />
                </div>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.relation}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-full"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Review"}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </section>
      )}

      {/* Featured Testimonial */}
      <section className="py-12">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12 relative overflow-hidden"
          >
            <Quote className="absolute top-8 right-8 w-24 h-24 text-white/10" />
            <div className="max-w-3xl">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-xl md:text-2xl font-serif leading-relaxed mb-6">
                "Konastone has cared for my Grandmother for almost a year now and throughout that time 
                the care that she has received has been exemplary. She has been treated with dignity, 
                kindness and all the carers have gone above and beyond to make sure she is happy and looked after."
              </p>
              <div className="flex items-center gap-4">
                <div>
                  <p className="font-semibold text-lg">Louise G</p>
                  <p className="text-white/70">Granddaughter of Client</p>
                </div>
                <span className="text-white/50 text-sm ml-auto">Homecare.co.uk</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="section-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                  <div className="flex gap-3 mb-4">
                    <div className="w-14 h-14 bg-gray-200 rounded-full" />
                    <div>
                      <div className="h-5 bg-gray-200 rounded w-32 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-24" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`testimonial-card-${index}`}
                  className="bg-white border border-primary/10 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-serif font-bold text-primary text-lg">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-serif font-semibold text-primary">
                          {testimonial.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">{testimonial.relation}</p>
                        <div className="flex mt-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                      </div>
                    </div>
                    {testimonial.source && (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                        {testimonial.source}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    "{testimonial.content}"
                  </p>
                  {testimonial.date && (
                    <p className="text-xs text-muted-foreground">{testimonial.date}</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              Experience Our Award-Winning Care
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Join our satisfied clients and discover the Konastone difference. 
              Book your free care assessment today.
            </p>
            <Link to="/contact">
              <Button
                data-testid="testimonials-cta-btn"
                className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 shadow-lg"
              >
                Book Free Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
